import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { User, Palette, Globe, Bell, Shield, Sun, Moon, Monitor } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { Card, CardBody } from "@/ui/Card";
import { Button } from "@/ui/Button";
import { Field, fieldInputClass } from "@/ui/modals/Modal";
import { cn } from "@/lib/cn";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { useUiStore, ACCENTS, type Theme, type Language, type Accent } from "@/stores/ui.store";
import { AGENCY, WALLET_ALERT_THRESHOLD_USD } from "@/lib/constants";
import type { Currency } from "@/types";
import { useSettings, useUpsertSetting } from "./settings.hooks";
import { getNumberSetting, getStringSetting, SETTING_KEYS } from "./settings.adapter";

type Section = "general" | "appearance" | "language" | "notifications" | "security";

const SECTIONS: { id: Section; label: string; icon: typeof User }[] = [
  { id: "general", label: "Général", icon: User },
  { id: "appearance", label: "Apparence", icon: Palette },
  { id: "language", label: "Langue", icon: Globe },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Sécurité", icon: Shield },
];

export function SettingsPage() {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>("general");

  return (
    <PageContainer>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />
      <div className="flex flex-col md:flex-row gap-6">
        <nav className="w-full md:w-[180px] shrink-0 flex md:flex-col gap-1 overflow-x-auto">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2.5 rounded-sm text-[13px] font-medium whitespace-nowrap transition-all",
                  section === s.id ? "bg-primary-dim text-primary" : "text-text2 hover:bg-bg4 hover:text-text",
                )}
              >
                <Icon className="h-4 w-4" /> {t(`settings.sections.${s.id}`)}
              </button>
            );
          })}
        </nav>
        <div className="flex-1 min-w-0">
          {section === "general" && <GeneralSettings />}
          {section === "appearance" && <AppearanceSettings />}
          {section === "language" && <LanguageSettings />}
          {section === "notifications" && <NotificationSettings />}
          {section === "security" && <SecuritySettings />}
        </div>
      </div>
    </PageContainer>
  );
}

function GeneralSettings() {
  const { settings } = useSettings();
  const upsert = useUpsertSetting();
  const [name, setName] = useState(AGENCY.name);
  const [email, setEmail] = useState(AGENCY.email);
  const [threshold, setThreshold] = useState(String(WALLET_ALERT_THRESHOLD_USD));
  const [currency, setCurrency] = useState<Currency>("DZD");

  // Hydrate from API settings once loaded (falls back to local defaults in mock mode).
  useEffect(() => {
    setName(getStringSetting(settings, SETTING_KEYS.agencyName, AGENCY.name));
    setEmail(getStringSetting(settings, SETTING_KEYS.agencyEmail, AGENCY.email));
    setThreshold(String(getNumberSetting(settings, SETTING_KEYS.walletThresholdUsd, WALLET_ALERT_THRESHOLD_USD)));
    const base = getStringSetting(settings, SETTING_KEYS.currencyBase, "DZD");
    if (base === "DZD" || base === "USD" || base === "EUR") setCurrency(base);
  }, [settings]);

  const saveGeneral = async () => {
    try {
      await Promise.all([
        upsert.mutateAsync({ key: SETTING_KEYS.agencyName, value: name }),
        upsert.mutateAsync({ key: SETTING_KEYS.agencyEmail, value: email }),
        upsert.mutateAsync({ key: SETTING_KEYS.walletThresholdUsd, value: Number(threshold) || 0 }),
      ]);
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'enregistrement"));
    }
  };

  const saveCurrency = async (c: Currency) => {
    setCurrency(c);
    try {
      await upsert.mutateAsync({ key: SETTING_KEYS.currencyBase, value: c });
      toast.success(`Monnaie: ${c}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'enregistrement"));
    }
  };

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <h3 className="text-sm font-bold text-text mb-1">Informations de l'agence</h3>
          <p className="text-xs text-text2 mb-4">Nom, contact et préférences générales</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nom de l'agence"><input className={fieldInputClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Email"><input className={fieldInputClass} value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
            <Field label="Seuil alerte wallet (USD)"><input type="number" className={fieldInputClass} value={threshold} onChange={(e) => setThreshold(e.target.value)} /></Field>
          </div>
          <div className="flex justify-end mt-3.5">
            <Button size="sm" onClick={saveGeneral}>Enregistrer</Button>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <h3 className="text-sm font-bold text-text mb-1">Monnaie de base</h3>
          <p className="text-xs text-text2 mb-4">Monnaie pour tous les calculs de profit</p>
          <div className="flex gap-2.5">
            {(["DZD", "EUR", "USD"] as Currency[]).map((c) => (
              <Button key={c} variant="ghost" size="md" className={currency === c ? "border-primary bg-primary-dim text-primary" : ""} onClick={() => saveCurrency(c)}>
                {currency === c ? "✓ " : ""}{c}
              </Button>
            ))}
          </div>
        </CardBody>
      </Card>
    </>
  );
}

const ACCENT_SWATCH: Record<Accent, string> = {
  indigo: "#6d7cff",
  violet: "#a879ff",
  blue: "#48b5ff",
  green: "#22c87a",
  amber: "#f0a030",
};

function AppearanceSettings() {
  const { t } = useTranslation();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const accent = useUiStore((s) => s.accent);
  const setAccent = useUiStore((s) => s.setAccent);
  const options: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: "light", label: t("settings.appearance.light"), icon: Sun },
    { value: "dark", label: t("settings.appearance.dark"), icon: Moon },
    { value: "system", label: t("settings.appearance.system"), icon: Monitor },
  ];
  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <h3 className="text-sm font-bold text-text mb-1">{t("settings.appearance.themeTitle")}</h3>
          <p className="text-xs text-text2 mb-4">{t("settings.appearance.themeDesc")}</p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            {options.map((o) => (
              <button
                key={o.value}
                onClick={() => { setTheme(o.value); toast.success(o.label); }}
                className={cn(
                  "flex flex-col items-center gap-2 px-5 py-4 rounded border-2 text-xs font-semibold transition-all",
                  theme === o.value ? "border-primary bg-primary-dim text-primary" : "border-border text-text2 hover:border-border2",
                )}
              >
                <o.icon className="h-5 w-5" />
                {o.label}
              </button>
            ))}
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <h3 className="text-sm font-bold text-text mb-1">{t("settings.appearance.accentTitle")}</h3>
          <p className="text-xs text-text2 mb-4">{t("settings.appearance.accentDesc")}</p>
          <div className="flex flex-wrap gap-3">
            {ACCENTS.map((a) => (
              <button
                key={a}
                onClick={() => setAccent(a)}
                aria-label={a}
                aria-pressed={accent === a}
                className={cn(
                  "h-9 w-9 rounded-full border-2 transition-all",
                  accent === a ? "border-text scale-110" : "border-transparent hover:scale-105",
                )}
                style={{ backgroundColor: ACCENT_SWATCH[a] }}
              >
                {accent === a && <span className="text-white text-xs font-bold">✓</span>}
              </button>
            ))}
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function LanguageSettings() {
  const { t } = useTranslation();
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const langs: { code: Language; flag: string; label: string; note?: string }[] = [
    { code: "fr", flag: "🇫🇷", label: "Français" },
    { code: "en", flag: "🇬🇧", label: "English" },
    { code: "ar", flag: "🇩🇿", label: "العربية", note: t("settings.language.rtlNote") },
  ];
  return (
    <Card>
      <CardBody>
        <h3 className="text-sm font-bold text-text mb-1">{t("settings.language.title")}</h3>
        <p className="text-xs text-text2 mb-4">{t("settings.language.desc")}</p>
        <div className="flex flex-col gap-2">
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLanguage(l.code); toast.success(l.label); }}
              className={cn(
                "flex items-center gap-3 px-3.5 py-3.5 rounded border text-left transition-all",
                language === l.code ? "border-2 border-primary bg-primary-dim" : "border border-border hover:border-border2",
              )}
            >
              <span className="text-lg">{l.flag}</span>
              <div className="flex-1">
                <div className="font-semibold text-text">{l.label}</div>
                {l.note && <div className="text-[11px] text-text3">{l.note}</div>}
              </div>
              {language === l.code && <span className="text-primary">✓</span>}
            </button>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function NotificationSettings() {
  const [prefs, setPrefs] = useState({ walletLow: true, latePayment: true, newTopup: true, weekly: false });
  const toggles: { key: keyof typeof prefs; title: string; desc: string }[] = [
    { key: "walletLow", title: "Solde wallet faible", desc: "Alerte quand le solde passe sous le seuil" },
    { key: "latePayment", title: "Paiement en retard", desc: "Rappel pour les factures impayées" },
    { key: "newTopup", title: "Nouveau rechargement", desc: "Confirmation de réception des fonds" },
    { key: "weekly", title: "Résumé hebdomadaire", desc: "Rapport de performance chaque lundi" },
  ];
  return (
    <Card>
      <CardBody>
        <h3 className="text-sm font-bold text-text mb-1">Préférences de notifications</h3>
        <p className="text-xs text-text2 mb-4">Contrôlez quand vous recevez des alertes</p>
        {toggles.map((t) => (
          <div key={t.key} className="flex items-center justify-between py-3 border-b border-border last:border-0">
            <div>
              <h4 className="text-[13px] font-semibold text-text">{t.title}</h4>
              <p className="text-[11px] text-text2">{t.desc}</p>
            </div>
            <button
              role="switch"
              aria-checked={prefs[t.key]}
              onClick={() => setPrefs((p) => ({ ...p, [t.key]: !p[t.key] }))}
              className={cn("relative w-9 h-5 rounded-full transition-colors", prefs[t.key] ? "bg-primary" : "bg-bg4 border border-border2")}
            >
              <span className={cn("absolute top-[3px] h-3.5 w-3.5 rounded-full bg-white transition-transform", prefs[t.key] ? "translate-x-[18px]" : "translate-x-[3px]")} />
            </button>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

function SecuritySettings() {
  return (
    <Card>
      <CardBody>
        <h3 className="text-sm font-bold text-text mb-1">Sécurité</h3>
        <p className="text-xs text-text2 mb-4">Mot de passe et authentification</p>
        <div className="flex flex-col gap-3 mb-4">
          <Field label="Mot de passe actuel"><input type="password" className={fieldInputClass} placeholder="••••••••" /></Field>
          <Field label="Nouveau mot de passe"><input type="password" className={fieldInputClass} placeholder="••••••••" /></Field>
        </div>
        <div className="flex justify-end mb-4">
          <Button size="sm" onClick={() => toast.success("Mot de passe mis à jour")}>Mettre à jour</Button>
        </div>
        <div className="rounded-sm border border-warning/25 bg-warning-dim px-4 py-3 text-xs text-warning">
          L'authentification multi-utilisateurs avec RBAC est gérée côté backend (voir docs/security.md).
        </div>
      </CardBody>
    </Card>
  );
}
